import type { FC } from 'react'

import { AssetFallbackIcon, Card, LoaderCircle, StyledText } from '@skytech/ui'
import { ImageWithIconFallback } from '@skytech/ui/Image'
import { useQuery } from '@tanstack/react-query'
import RecycleIcon from 'mdi-react/RecycleIcon'
import { useParams } from 'react-router-dom'
import { twJoin } from 'tailwind-merge'

import queries from '@/api/queries'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import { useTranslation } from '@/components/i18n/hooks'
import { Translation } from '@/components/i18n/Translation'
import { LoadingError } from '@/components/LoadingError'
import { LocalDate } from '@/components/LocalDate'
import Page from '@/components/page/Page'
import { useIsThreeStepIT } from '@/hooks/user'
import { ecomPolicyResources } from '@/resources/ecom-policy'

export const GreenpoolOrderDetails: FC = () => {
  const translate = useTranslation()
  const isThreeStepIT = useIsThreeStepIT()
  const params = useParams()
  const orderId = Number(params.orderId)

  if (isNaN(orderId)) throw Error(translate('Order ID is not a number!'))

  const { data, isError } = useQuery(queries.greenpoolOrder(orderId), () =>
    ecomPolicyResources.getGreenpoolOrder(orderId)
  )

  if (isError) return <LoadingError />
  if (!data) return <LoaderCircle />

  const { id, orderDate, policyName, customerName, products } = data

  return (
    <Page pageWrapperClassName="max-w-3xl xl:pr-4">
      <h2 className="text-2xl">
        <Translation id="Greenpool order #{orderId}" orderId={id} />
      </h2>

      <div className="m-4 rounded-lg bg-white p-4 text-black max-md:m-2 max-[350px]:m-0">
        <section className="grid grid-cols-[repeat(auto-fill,_minmax(9rem,_1fr))] rounded-xl p-3 shadow-[0_2px_8px_0_rgba(99,99,99,0.2)]">
          <div className="m-2 min-w-32 flex-1 basis-32">
            <h4 className="m-0 mb-1 text-xs font-normal leading-none">
              <Translation id="Ordredato" />
            </h4>
            <p className="!m-0 !mt-0.5 text-base leading-none">
              <LocalDate date={orderDate} />
            </p>
          </div>
          {Boolean(policyName) && (
            <div className="m-2 min-w-32 flex-1 basis-32">
              <h4 className="m-0 mb-1 text-xs font-normal leading-none">
                <Translation id="Policy" />
              </h4>
              <p className="!m-0 !mt-0.5 text-base leading-none">
                <small>{policyName}</small>
              </p>
            </div>
          )}

          {Boolean(customerName) && (
            <div className="m-2 min-w-32 flex-1 basis-32">
              <h4 className="m-0 mb-1 text-xs font-normal leading-none">
                <Translation id="Customer" />
              </h4>
              <p className="!m-0 !mt-0.5 text-base leading-none">
                <small>{customerName}</small>
              </p>
            </div>
          )}
        </section>

        <h3 className="!mx-4 !mb-4 !mt-8 text-xl font-semibold leading-none text-black opacity-80">
          <Translation id="Produktliste" />
        </h3>

        <div className="grid gap-4 rounded-lg p-0">
          {products.map(product => (
            <Card key={product.productName} noMargin className="flex w-full justify-between">
              <div className="flex w-full flex-col items-center sm:flex-row">
                <div
                  className={twJoin(
                    'flex flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-white p-2 transition-all duration-200 ease-in-out',
                    'h-16 w-16 max-md:h-12 max-md:w-12 max-sm:mb-3 max-sm:h-24 max-sm:w-24',
                    'shadow-[0_2px_4px_-1px_rgba(0,0,0,0.15),0_6px_8px_0_rgba(0,0,0,0.05),0_1px_10px_0_rgba(0,0,0,0.10)]'
                  )}
                >
                  <ImageWithIconFallback
                    fallbackIcon={<AssetFallbackIcon assetType={product.assetType || 'OTHER'} />}
                    src={product.articleImageUrl || ''}
                    alt="Product"
                    className="flex max-w-full items-center justify-center"
                  />
                </div>

                <div className="w-full text-left sm:ml-4">
                  <h4 className="m-0 mt-0.5 text-lg font-normal leading-none text-black md:text-xl">
                    {product.productName}
                  </h4>

                  <StyledText colorMode="gray" size="small" block marginTop={2}>
                    <Translation id="produktnr" /> {product.articleNo}
                  </StyledText>

                  <div className="mt-1.5">
                    {product.quantity && (
                      <StyledText size="large" marginRight={8}>
                        {product.quantity}x
                      </StyledText>
                    )}
                    {product.cost > 0 ? (
                      <FormattedNumberWithCurrency amount={product.cost} />
                    ) : (
                      <span> — </span>
                    )}
                  </div>
                </div>
              </div>
              <div>
                <RecycleIcon
                  className={twJoin(isThreeStepIT ? 'fill-threeStepIT' : 'fill-green-badge')}
                />
              </div>
            </Card>
          ))}
        </div>
      </div>
    </Page>
  )
}
